"use client";

import { useEffect, useState } from "react";
import { useClientValue } from "@/lib/use-client-value";
import Image from "next/image";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import { storeLinks } from "@/components/sections/app-download";
import { QrCode } from "lucide-react";
import { useTranslation } from "../language/hooks/useTranslation";
import { useQrPopupStore } from "@/store/useQrPopupStore";

const LOCAL_STORAGE_KEY = "qr-popup-collapsed";
const SHOW_DELAY_MS = 300;

function readCollapsed() {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY) === "true";
  } catch {
    // localStorage throws in private browsing and when site data is blocked.
    return false;
  }
}

export default function QrPopup() {
  const { open, openPopup, closePopup } = useQrPopupStore();
  // Whether the visitor collapsed this before is stored per browser. It is
  // read rather than set from an effect; the buttons below override it.
  const storedCollapsed = useClientValue(readCollapsed, false);
  const [override, setOverride] = useState<boolean | null>(null);
  const collapsed = override ?? storedCollapsed;
  const { t, tm } = useTranslation();
  const feat = tm("popup.features") as string[];

  // Opening the popup is a side effect, so it stays in an effect. It is skipped
  // for anyone who has collapsed it before.
  useEffect(() => {
    if (readCollapsed()) return;
    const timer = setTimeout(() => openPopup(), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [openPopup]);

  const remember = (value: boolean) => {
    setOverride(value);
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, String(value));
    } catch {
      // Not being able to remember the choice is not worth failing the click.
    }
  };

  const handleCollapse = () => {
    closePopup();
    remember(true);
  };

  const handleOpen = () => {
    openPopup();
    remember(false);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="qr-modal"
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-brand-bg/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            aria-modal="true"
            aria-labelledby="qr-modal-title"
            aria-describedby="qr-modal-desc"
            role="dialog"
          >
            <motion.div
              layoutId="qr-popup"
              initial={{ opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative w-full max-w-xl overflow-hidden global-border-radius border border-brand-teal bg-brand-text shadow-2xl"
            >
              <div
                className="absolute inset-0 py-16 bg-brand-bg"
                aria-hidden
              />
              <div className="relative grid gap-6 px-4 py-4 sm:px-8 sm:py-8">
                <button
                  type="button"
                  onClick={handleCollapse}
                  aria-label="Close QR popup"
                  className="absolute right-4 top-4 rounded-full border border-brand-grey bg-brand-text px-2 py-1 text-sm text-brand-bg shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
                >
                  ✕
                </button>

                <div className="space-y-2 text-center">
                  <p className="text-sm font-bold text-brand-text 3xl:text-xl">Scan & download</p>
                  <h3 className="text-2xl font-semibold text-brand-text 3xl:text-5xl 3xl:py-5" id="qr-modal-title">Vell App</h3>
                  <p className="max-sm:text-xs text-sm text-brand-text/80 3xl:text-lg 3xl:py-2" id="qr-modal-desc">
                    Point your camera to open the link, or tap a store badge. Built for quick, secure
                    transfers.
                  </p>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-brand-text/80 3xl:text-sm max-xs:hidden">
                  {feat?.map((item) => (
                    <span
                      key={item}
                      className="px-3 py-1 font-medium border rounded-full shadow-sm border-brand-card bg-brand-card"
                    >
                      {item}
                    </span>
                  ))}
                </div>

                <div className="grid gap-4 sm:grid-cols-[1fr,1fr] sm:items-center sm:gap-6">
                  <div className="relative mx-auto aspect-square w-full max-w-[170px] overflow-hidden global-border-radius bg-white shadow-sm">
                    <Image
                      src="/QR-Download.png"
                      alt={t("popup.qrAlt")}
                      fill
                      className="object-contain"
                      sizes="(max-width: 768px) 160px, 170px"
                      priority
                      fetchPriority="high"
                    />
                  </div>

                  <div className="space-y-4 sm:space-y-5">
                    <div className="flex max-sm:flex-wrap justify-center">
                      {storeLinks.map((link) => (
                        <Link
                          key={link.name}
                          href={link.href}
                          className="group inline-flex items-center justify-center global-border-radius px-1 md:px-2 py-1 transition hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
                          aria-label={`Open ${link.name}`}
                        >
                          <Image
                            src={link.src}
                            alt={link.name}
                            width={150}
                            height={45}
                            className="w-full max-w-[135px] xs:max-w-[150px] max-w-[125px] min-h-[48px] xs:min-h-[50px] sm:min-h-[52px]"
                            sizes="(max-width: 640px) 160px, 180px"
                            priority={link.name === "App Store"}
                          />
                        </Link>
                      ))}
                    </div>
                    <p className="text-xs text-center text-brand-text/80 3xl:text-sm">
                      {t("popup.storeHint")}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {collapsed && (
          <motion.div
            layoutId="qr-popup"
            key="qr-collapsed"
            className="fixed z-50 bottom-6 left-6"
            initial={{ opacity: 0, scale: 0.5, x: -50, y: 50 }}
            animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, x: -50, y: 50 }}
            transition={{ type: "spring", stiffness: 150, damping: 30 }}
          >
            <button
              onClick={handleOpen}
              aria-label="Open QR popup"
              className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-text shadow-lg text-brand-card text-xl font-bold hover:scale-110 transition-transform"
            >
              <QrCode className="w-6 h-6" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
